import { useMutation } from '@tanstack/react-query';

import { agentLogsQueryKeys, collectExecutionToDataset } from '~/api/agentLogs';
import { isDatasetLockError } from '~/api/agentLogs/errors';
import type {
  AgentExecutionDetail,
  AgentExecutionLogEntry,
  CollectTestCasePayload
} from '~/api/agentLogs/types';
import { parseAgentInputMessageConfig } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/utils/logParser';

type UseCollectAgentExecutionParams = {
  realm: string;
  environment: string;
  detail: AgentExecutionDetail | undefined;
  datasetId: string | null;
};

function extractUserMessage(logs: AgentExecutionLogEntry[]): string | null {
  const userMessage = logs
    .map(log => parseAgentInputMessageConfig(log.body))
    .find(config => config?.messageType === 'user');

  return userMessage?.content ?? null;
}

function buildCollectPayload(
  detail: AgentExecutionDetail
): CollectTestCasePayload {
  const userMessage = extractUserMessage(detail.logs);

  if (!userMessage) {
    throw new Error('User message is required to collect a test case');
  }

  return {
    name: `${detail.agentName} - ${detail.startedAt}`,
    variables: {
      message: userMessage
    }
  };
}

function parsePipelineVersion(version: string): number {
  const parsed = Number.parseFloat(version);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function useCollectAgentExecution({
  realm,
  environment,
  detail,
  datasetId
}: UseCollectAgentExecutionParams) {
  return useMutation({
    mutationKey: agentLogsQueryKeys.collect(),
    mutationFn: async () => {
      if (!detail || !datasetId) {
        throw new Error('Detail and dataset are required to collect execution');
      }

      return collectExecutionToDataset({
        realm,
        environment,
        pipelineName: detail.pipelineName,
        pipelineVersion: parsePipelineVersion(detail.pipelineVersion),
        connectorId: detail.connectorId,
        datasetId,
        agentExecutionKey: detail.agentExecutionKey,
        pipelineKey: detail.pipelineKey,
        payload: buildCollectPayload(detail)
      });
    }
  });
}

export { isDatasetLockError };
